import { useEffect, useMemo } from 'react';
import { AppointmentType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/appointment_type.pb';
import { ValueType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/mapping.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { useGetAppointmentColorsEnabled } from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import {
  FormRow,
  Modal,
  ModalControlModalProps,
  NumberField,
  Text,
  TextField,
  TextareaField,
  useAlert,
  useForm,
} from '@frontend/design-system';
import { PRIMARY_EVENT_COLOR } from '../../constants';
import { useGetCustomContactSourceId } from '../../hooks/scheduler-v3/use-get-custom-contact-source-id';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { useAppointmentTypesHelperShallowStore } from '../../stores/use-appointment-types-helper-store';
import { AppointmentTypesV3TrackingIds } from '../../trackingIds/appointment-types-v3-trackingids';
import { usePractitionerCheckListDropdown } from '../PractitionersCheckListDropdown';
import { ResponsiveModalFooter } from '../settings';
import { AppointmentTypeColorPicker } from './appointment-type-color-picker/AppointmentTypeColorPicker';
import { AppointmentTypePaymentFields } from './appointment-type-payment-fields/appointment-type-payment-fields';
import { useAppointmentTypePaymentFields } from './appointment-type-payment-fields/use-appointment-type-payment-fields';

interface Props extends ModalControlModalProps {
  locationId: string;
  data?: AppointmentType;
}

export const AddEditAppointmentTypeModal = ({ locationId, data, ...modalProps }: Props) => {
  const alert = useAlert();
  const { t } = useTranslation('scheduleAppointmentTypes');
  const isEdit = Boolean(data?.id);

  const { parentLocationId, isMultiLocation } = useSchedulingLocationInfo();
  const { customContactSourceId } = useGetCustomContactSourceId();
  const { hasWSM } = SchedulerV3.Helpers.useCheckHasWSM(locationId);
  const { canUseAppointmentColors } = useGetAppointmentColorsEnabled();
  const { isPaymentsEnabled, isOnlineSchedulingEnabled } = useAppointmentTypesHelperShallowStore(
    'isPaymentsEnabled',
    'isOnlineSchedulingEnabled'
  );

  const invalidateQueries = SchedulerV3.Helpers.useSchedulerV3InvalidateQueries();
  const createMutation = SchedulerV3.Mutations.useCreateAppointmentTypeMutation();
  const updateMutation = SchedulerV3.Mutations.useUpdateAppointmentTypeMutation({
    options: {
      onSuccess: () => {
        // invalidate calendar events for this appointment type to reflect any changes
        invalidateQueries.invalidateCalendarEventsForApptType(locationId);
      },
    },
  });
  const { createAndDeleteMappingsAsync, isLoading: isCreatingMappings } =
    SchedulerV3.Mutations.useCreateAndDeleteMappings();

  const {
    PractitionerCheckListDropdownElement,
    getPractitionerSelectedUnselectedValues,
    resetSelection: resetProviderSelection,
    hasPractitionerCheckListDropdownError,
  } = usePractitionerCheckListDropdown({ locationId: locationId, appointmentTypeId: data?.id });

  const {
    formProps,
    getFieldProps,
    values,
    reset: resetForm,
    isComplete,
    seedValues,
  } = useForm({
    fields: {
      appointmentTypeColor: {
        type: 'text',
        hidden: !canUseAppointmentColors,
      },
      displayName: {
        type: 'text',
        required: true,
      },
      externalName: {
        type: 'text',
      },
      duration: {
        type: 'number',
        required: hasWSM,
        includeThousandsSeparator: false,
        ...(hasWSM ? { max: 1440, min: 5, allowDecimal: false, allowNegative: false } : undefined),
      },
      description: {
        type: 'text',
        required: false,
      },
    },
  });
  const paymentFields = useAppointmentTypePaymentFields(data?.details?.bda);

  const resetFormValues = () => {
    resetForm();
    paymentFields.reset();
    resetProviderSelection();
  };

  useEffect(() => {
    if (!modalProps.show) {
      resetFormValues();
    }
  }, [modalProps.show]);

  useEffect(() => {
    if (data?.id) {
      seedValues({
        externalName: data?.externalName || '',
        displayName: data?.displayName || '',
        duration: (data?.details?.dur || 0).toString(),
        description: data?.description || '',
        appointmentTypeColor: canUseAppointmentColors ? data?.details?.clr || PRIMARY_EVENT_COLOR : undefined,
      });
    }
  }, [canUseAppointmentColors, data, seedValues]);

  const handleCreateAppointmentType = async () => {
    try {
      const appointmentType = await createMutation.mutateAsync({
        displayName: values.displayName || '',
        locationId: locationId,
        sourceTenantId: locationId,
        description: values.description,
        details: {
          act: true,
          bda: paymentFields.depositAmount,
          dur: values.duration ? parseInt(values.duration) : 0,
          // NOTE: Setting sobs to false by default. An office will need to toggle the table column 'Online Scheduling' to on after they create an appointment type. "sobs" means the appointment type will not be shown on the booking site.
          sobs: false,
          clr: canUseAppointmentColors ? values.appointmentTypeColor : undefined,
        },
        hidden: false,
        // for non-integrated location, we need to set the sourceId as customContactSourceId,
        // for integrated location, BE set the sourceId as the integration's SourceID automatically and on FE we were not allowing create provider for integrated location
        sourceId: customContactSourceId,
        parentLocationId: isMultiLocation ? parentLocationId : undefined,
      });
      if (appointmentType) {
        const { newSelectedProviderIds, mappingIdsForDelete } = getPractitionerSelectedUnselectedValues();
        await createAndDeleteMappingsAsync({
          appointmentTypeId: appointmentType?.appointmentType?.id || '',
          locationId: locationId,
          values: newSelectedProviderIds,
          valueType: ValueType.VT_PROVIDER,
          mappingIdsForDelete,
        });
      }
      alert.success(t('Appointment Type created successfully'));
      modalProps.onClose();
    } catch (error) {
      alert.error(t('Failed to create appointment type'));
    }
  };

  const getUpdateAppointmentTypePayload = (): SchedulerV3.Types.UpdateAppointmentTypeIO['input'] | null => {
    if (!data) return null;

    const payload = {
      appointmentTypeId: data.id,
      description: values.description,
      displayName: values.displayName,
      childLocationId: isMultiLocation ? data?.locationId || '' : undefined,
      hidden: false,
    };

    if (isOnlineSchedulingEnabled && isPaymentsEnabled && !hasWSM) {
      return {
        ...payload,
        details: {
          ...data.details,
          bda: paymentFields.depositAmount,
          clr: canUseAppointmentColors ? values.appointmentTypeColor : undefined,
        },
      };
    }

    if (isOnlineSchedulingEnabled && isPaymentsEnabled && hasWSM) {
      return {
        ...payload,
        details: {
          ...data.details,
          dur: parseInt(values.duration || '0'),
          bda: paymentFields.depositAmount,
          clr: canUseAppointmentColors ? values.appointmentTypeColor : undefined,
        },
      };
    }

    if (hasWSM) {
      return {
        ...payload,
        details: {
          ...data.details,
          dur: parseInt(values.duration || '0'),
          clr: canUseAppointmentColors ? values.appointmentTypeColor : undefined,
        },
      };
    }

    return payload;
  };

  const handleUpdateAppointmentType = () => {
    const payload = getUpdateAppointmentTypePayload();
    if (!payload || !data) return;

    const { mappingIdsForDelete, newSelectedProviderIds } = getPractitionerSelectedUnselectedValues();
    const updateAppointmentTypeMethod = () => updateMutation.mutateAsync(payload);

    const updateAppointmentTypePractitionersMapping = () =>
      createAndDeleteMappingsAsync({
        locationId: locationId,
        appointmentTypeId: data.id,
        values: newSelectedProviderIds,
        valueType: ValueType.VT_PROVIDER,
        mappingIdsForDelete: mappingIdsForDelete,
      });

    return Promise.all([updateAppointmentTypeMethod(), updateAppointmentTypePractitionersMapping()])
      .then(() => {
        modalProps.onClose();
        alert.success(t('Appointment type updated successfully'));
      })
      .catch(() => {
        alert.error(t('Failed to update appointment type'));
      });
  };

  const isFormComplete = isComplete && !hasPractitionerCheckListDropdownError && paymentFields.isFormComplete;
  const isLoading = createMutation.isLoading || updateMutation.isLoading || isCreatingMappings;

  const headerSubtitle = useMemo(() => {
    if (!isEdit) {
      return t(
        'You can create an appointment type. The appointment type will be reflected in your Weave calendar and online booking system.'
      );
    }
    if (!hasWSM) {
      return isOnlineSchedulingEnabled
        ? t(
            'Modify the details of your appointment type. These changes will be reflected in your Weave calendar and online booking system, but will not sync to your practice management system.'
          )
        : t(
            'Modify the details of your appointment type. These changes will be reflected in your Weave calendar, but will not sync to your practice management system.'
          );
    }
    return isOnlineSchedulingEnabled
      ? t(
          'Modify the details of your appointment type. These changes will be reflected in your Weave calendar and online booking system.'
        )
      : t('Modify the details of your appointment type. These changes will be reflected in your Weave calendar.');
  }, [hasWSM, isEdit, isOnlineSchedulingEnabled, t]);

  return (
    <Modal.Responsive {...modalProps} minWidth={600} onClose={modalProps.onClose}>
      <Modal.Header onClose={modalProps.onClose}>
        {isEdit ? t('Edit Appointment Type') : t('Create Appointment Type')}
      </Modal.Header>
      <Modal.Body>
        <form {...formProps}>
          <Text size='small' css={{ marginBottom: theme.spacing(2) }}>
            {headerSubtitle}
          </Text>
          {!hasWSM && (
            <FormRow css={{ marginTop: theme.spacing(3) }}>
              <TextField disabled label={t('Appointment Type Name')} {...getFieldProps('externalName')} />
            </FormRow>
          )}
          <div
            css={{
              display: 'flex',
              gap: theme.spacing(2),
              flexWrap: 'wrap',
              marginBottom: theme.spacing(2),
              '> div:nth-child(2)': { flex: '1 0 268px' },
              '> div:nth-child(3)': {
                display: 'flex',
                columnGap: theme.spacing(1),
                alignItems: 'start',
                flexWrap: 'wrap',
                flex: '1 0',
                alignContent: 'flex-start',
              },
            }}
          >
            {canUseAppointmentColors && (
              <AppointmentTypeColorPicker
                color={values.appointmentTypeColor || PRIMARY_EVENT_COLOR}
                onChange={(color) =>
                  getFieldProps('appointmentTypeColor').onChange({ value: color, name: 'appointmentTypeColor' })
                }
              />
            )}
            <TextField
              label={t('Display Name')}
              {...getFieldProps('displayName')}
              helperText={
                isOnlineSchedulingEnabled
                  ? t('This name will be visible to customers when booking online, and on your Weave calendar.')
                  : t('Enter the name for this appointment type. It will be displayed on your Weave calendar.')
              }
            />
            <NumberField
              disabled={!hasWSM}
              label={t('Duration')}
              helperText={t('Minutes')}
              {...getFieldProps('duration')}
              className='duration-number-field'
              css={{ width: '120px' }}
            />
          </div>
          {isPaymentsEnabled && isOnlineSchedulingEnabled && (
            <AppointmentTypePaymentFields
              getFieldProps={paymentFields.getFieldProps}
              appointmentTypeLocationId={locationId}
            />
          )}
          {PractitionerCheckListDropdownElement}
          <TextareaField label={t('Description')} {...getFieldProps('description')} />
          <Text size='small' color='light' css={{ marginTop: theme.spacing(1) }}>
            {isOnlineSchedulingEnabled
              ? t('Describe this appointment type. Customers will see this information when booking online')
              : t('Describe this appointment type.')}
          </Text>
        </form>
      </Modal.Body>

      <ResponsiveModalFooter
        primary={{
          loading: isLoading,
          label: isEdit ? t('Save') : t('Create'),
          onClick: isEdit ? handleUpdateAppointmentType : handleCreateAppointmentType,
          disabled: !isFormComplete,
          trackingId: isEdit
            ? AppointmentTypesV3TrackingIds.editAppointmentTypeModalSaveBtn
            : AppointmentTypesV3TrackingIds.createAppointmentTypeModalCreateBtn,
        }}
        secondary={{
          label: t('Cancel'),
          onClick: modalProps.onClose,
          disabled: isLoading,
          trackingId: isEdit
            ? AppointmentTypesV3TrackingIds.editAppointmentTypeModalCancelBtn
            : AppointmentTypesV3TrackingIds.createAppointmentTypeModalCancelBtn,
        }}
      />
    </Modal.Responsive>
  );
};
